import {
  Alert,
  Button,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BattleManager } from "../game/assets/pri_wqs/scripts/core/BattleManager";
import type { RuntimePropInstance } from "../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import type { PropRuntimeGraph } from "../game/assets/shared/types/PropRuntimeGraph";
import {
  createSandboxBattleManager,
  disposeSandboxBattleManager,
  loadPropRuntimeGraphs,
  SANDBOX_TAG_LABELS,
} from "./propRuntimeSandbox/runtimeBridge";
import type { SandboxCellSelection } from "./propRuntimeSandbox/types";
import {
  HarmonyButton,
  SandboxColumn,
  SandboxLayout,
  SandboxRoot,
  SandboxToolbar,
} from "./propRuntimeSandbox/styles";
import { ChoiceModal } from "./propRuntimeSandbox/ChoiceModal";
import { BoardPanel } from "./propRuntimeSandbox/BoardPanel";
import { ConsumablesPanel } from "./propRuntimeSandbox/ConsumablesPanel";
import { EventLogPanel } from "./propRuntimeSandbox/EventLogPanel";
import { MaterialLibraryPanel } from "./propRuntimeSandbox/MaterialLibraryPanel";
import {
  isMagicGainEffectPlaying,
  MagicGainEffects,
  waitForMagicGainEffects,
} from "./propRuntimeSandbox/MagicGainEffects";
import { RecipesPanel } from "./propRuntimeSandbox/RecipesPanel";
import { StatsBar } from "./propRuntimeSandbox/StatsBar";
import { StatusPanel } from "./propRuntimeSandbox/StatusPanel";
import {
  CommissionSummaryModal,
  type CommissionSummaryData,
} from "./propRuntimeSandbox/CommissionSummaryModal";
import { pickRefreshedChoiceGraphs, pickWeightedUniqueGraphs } from "./propRuntimeSandbox/choiceUtils";
import {
  getCommissionRoundLimit,
  SANDBOX_COMMISSION_ROUNDS,
  type SandboxChoiceKind,
  type SandboxFlowStage,
} from "./propRuntimeSandbox/flowUtils";

const { Text, Title } = Typography;

const BOARD_ROWS = 4;
const BOARD_COLS = 5;
const HAND_SLOT_COUNT = 6;
const CHOICE_COUNT = 3;
const JUNK_TAG_ID = 99;
const EMPTY_PROP_NAME = "空位置";
const SELECTABLE_SYSTEM_TAGS = [1, 2, 3, 4, 5, 6];
const DEFAULT_SYSTEM_TAGS = [1, 3, 6];
const CHOICE_ANIMATION_FALLBACK_MS = 30000;
const FORCE_REFRESH_STORAGE_KEY = "propRuntimeSandbox.forceRefreshGraphIds";

interface PendingChoiceRequest {
  kind: SandboxChoiceKind;
  options: PropRuntimeGraph[];
  stage?: SandboxFlowStage;
  commissionIndex?: number;
}

type ForceRefreshGraphIds = Partial<Record<SandboxChoiceKind, string>>;

/** 游戏沙盒页面只负责编排游戏流程，显示模块通过 BattleManager 事件自行刷新。 */
export default function PagePropRuntimeSandbox() {
  const [catalog, setCatalog] = useState<PropRuntimeGraph[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selectedCell, setSelectedCell] = useState<SandboxCellSelection | undefined>();
  const [selectedInstanceId, setSelectedInstanceId] = useState("");
  const [selectedSystemTags, setSelectedSystemTags] = useState<number[]>(DEFAULT_SYSTEM_TAGS);
  const [gameStarted, setGameStarted] = useState(false);
  const [flowStage, setFlowStage] = useState<SandboxFlowStage>("idle");
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [choiceKind, setChoiceKind] = useState<SandboxChoiceKind>("recipe");
  const [choiceOptions, setChoiceOptions] = useState<PropRuntimeGraph[]>([]);
  const [choiceRefreshing, setChoiceRefreshing] = useState(false);
  const [forceRefreshGraphIds, setForceRefreshGraphIds] = useState<ForceRefreshGraphIds>(() => readForceRefreshGraphIds());
  const [pendingCommissionIndex, setPendingCommissionIndex] = useState(1);
  const [lastConsumableChoiceRoundKey, setLastConsumableChoiceRoundKey] = useState("");
  const [commissionSnapshot, setCommissionSnapshot] = useState<CommissionSnapshot | undefined>();
  const [commissionSummary, setCommissionSummary] = useState<CommissionSummaryData | undefined>();
  const [commissionSummaryOpen, setCommissionSummaryOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState(false);
  const [roundRunning, setRoundRunning] = useState(false);
  const [magicEffectPlaying, setMagicEffectPlaying] = useState(false);
  const [manager, setManager] = useState<BattleManager | undefined>();
  const pendingChoiceRef = useRef<PendingChoiceRequest | undefined>(undefined);
  const pendingChoiceRequestIdRef = useRef(0);
  const roundRunRequestIdRef = useRef(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadPropRuntimeGraphs()
      .then((items) => {
        if (!alive) return;
        setCatalog(items);
        setLoadError("");
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err?.message ?? String(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      roundRunRequestIdRef.current += 1;
      disposeSandboxBattleManager();
    };
  }, []);

  const systemOptions = useMemo(
    () => SELECTABLE_SYSTEM_TAGS.map((value) => ({
      value,
      label: SANDBOX_TAG_LABELS[value] ?? `体系${value}`,
      disabled: selectedSystemTags.length >= 3 && !selectedSystemTags.includes(value),
    })),
    [selectedSystemTags],
  );

  const managerState = manager?.getState();
  const effectiveCommissionIndex = managerState?.commissionIndex || pendingCommissionIndex;
  const currentRoundIndex = managerState?.roundIndex ?? 0;
  const currentConsumableCount = managerState?.consumables.length ?? 0;
  const roundLimit = getCommissionRoundLimit(
    effectiveCommissionIndex,
    managerState?.extraCommissionRounds ?? 0,
  );
  const updateForceRefreshGraphId = useCallback((kind: SandboxChoiceKind, graphId?: string) => {
    setForceRefreshGraphIds((previous) => {
      const next: ForceRefreshGraphIds = { ...previous };
      if (graphId) next[kind] = graphId;
      else delete next[kind];
      writeForceRefreshGraphIds(next);
      return next;
    });
  }, []);
  const forceRefreshOptions = useMemo(() => {
    if (!manager || !choiceOpen) return [];
    return getChoiceCandidates(choiceKind, manager.getState());
  }, [choiceKind, choiceOpen, manager]);
  const forceRefreshValue = forceRefreshOptions.some((graph) => graph._id === forceRefreshGraphIds[choiceKind])
    ? forceRefreshGraphIds[choiceKind]
    : undefined;

  useEffect(() => {
    if (!choiceOpen || !forceRefreshOptions.length) return;
    const graphId = forceRefreshGraphIds[choiceKind];
    if (graphId && !forceRefreshOptions.some((graph) => graph._id === graphId)) {
      updateForceRefreshGraphId(choiceKind, undefined);
    }
  }, [choiceKind, choiceOpen, forceRefreshGraphIds, forceRefreshOptions, updateForceRefreshGraphId]);

  const openChoiceImmediately = useCallback((request: PendingChoiceRequest) => {
    if (typeof request.commissionIndex === "number") {
      setPendingCommissionIndex(request.commissionIndex);
    }
    setChoiceKind(request.kind);
    setChoiceOptions(request.options);
    setChoiceOpen(true);
    if (request.stage) setFlowStage(request.stage);
  }, []);

  const clearPendingChoice = useCallback(() => {
    pendingChoiceRef.current = undefined;
    pendingChoiceRequestIdRef.current += 1;
  }, []);

  const flushPendingChoice = useCallback((requestId: number) => {
    if (pendingChoiceRequestIdRef.current !== requestId) return;
    const request = pendingChoiceRef.current;
    pendingChoiceRef.current = undefined;
    if (request) openChoiceImmediately(request);
  }, [openChoiceImmediately]);

  const openChoiceWhenEffectsComplete = useCallback((request: PendingChoiceRequest) => {
    const requestId = pendingChoiceRequestIdRef.current + 1;
    pendingChoiceRequestIdRef.current = requestId;
    pendingChoiceRef.current = request;

    if (!isMagicGainEffectPlaying()) {
      flushPendingChoice(requestId);
      return;
    }
    waitForMagicGainEffects(CHOICE_ANIMATION_FALLBACK_MS).then(() => flushPendingChoice(requestId));
  }, [flushPendingChoice]);

  useEffect(() => () => {
    clearPendingChoice();
  }, [clearPendingChoice]);

  const startSandbox = () => {
    if (!catalog.length) {
      message.warning("运行时道具数据尚未加载");
      return;
    }
    if (selectedSystemTags.length !== 3) {
      message.warning("请选择 3 个不同体系");
      return;
    }
    try {
      disposeSandboxBattleManager();
      const scopedCatalog = getScopedCatalog(catalog, selectedSystemTags);
      const nextManager = createSandboxBattleManager(scopedCatalog, {
        rows: BOARD_ROWS,
        cols: BOARD_COLS,
      });
      setSelectedCell(undefined);
      setSelectedInstanceId("");
      setRoundRunning(false);
      setManager(nextManager);
      setGameStarted(true);
      setPendingStart(true);
    } catch (err: any) {
      message.error(err?.message ?? String(err));
    }
  };

  const endSandbox = () => {
    clearPendingChoice();
    roundRunRequestIdRef.current += 1;
    manager?.endGame();
    disposeSandboxBattleManager();
    setManager(undefined);
    setSelectedCell(undefined);
    setSelectedInstanceId("");
    setChoiceOpen(false);
    setChoiceOptions([]);
    setChoiceKind("recipe");
    setPendingCommissionIndex(1);
    setLastConsumableChoiceRoundKey("");
    setCommissionSnapshot(undefined);
    setCommissionSummary(undefined);
    setCommissionSummaryOpen(false);
    setPendingStart(false);
    setRoundRunning(false);
    setMagicEffectPlaying(false);
    setFlowStage("idle");
    setGameStarted(false);
  };

  const openRecipeChoice = useCallback((targetManager: BattleManager, commissionIndex: number) => {
    const currentState = targetManager.getState();
    setCommissionSnapshot(createCommissionSnapshot(currentState));
    const candidates = getChoiceCandidates("recipe", currentState);
    const options = pickWeightedUniqueGraphs(candidates, CHOICE_COUNT, currentState, commissionIndex);
    setPendingCommissionIndex(commissionIndex);
    if (!options.length) {
      message.warning(`第 ${commissionIndex} 个委托没有可选配方，直接开始委托`);
      setFlowStage("beforeRound");
      setRoundRunning(false);
      return;
    }
    openChoiceWhenEffectsComplete({
      kind: "recipe",
      options,
      stage: "recipeChoice",
      commissionIndex,
    });
  }, [openChoiceWhenEffectsComplete]);

  useEffect(() => {
    if (!pendingStart || !manager) return undefined;
    const timer = window.setTimeout(() => {
      try {
        manager.startGame();
        manager.startCommission(1);
        openRecipeChoice(manager, 1);
        message.success("游戏已开始");
      } catch (err: any) {
        message.error(err?.message ?? String(err));
      } finally {
        setPendingStart(false);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [manager, openRecipeChoice, pendingStart]);

  const openMaterialChoice = (targetManager: BattleManager, roundRunRequestId?: number) => {
    const currentState = targetManager.getState();
    const candidates = getChoiceCandidates("material", currentState);
    const options = pickWeightedUniqueGraphs(candidates, CHOICE_COUNT, currentState);
    if (!options.length) {
      waitForMagicGainEffects(CHOICE_ANIMATION_FALLBACK_MS).then(() => {
        if (roundRunRequestId !== undefined && roundRunRequestIdRef.current !== roundRunRequestId) return;
        if (finishRoundReward(targetManager)) setRoundRunning(false);
      });
      return;
    }
    openChoiceWhenEffectsComplete({
      kind: "material",
      options,
      stage: "materialChoice",
    });
  };

  const openConsumableChoice = (targetManager: BattleManager) => {
    const currentState = targetManager.getState();
    const candidates = getChoiceCandidates("consumable", currentState);
    const options = pickWeightedUniqueGraphs(candidates, CHOICE_COUNT, currentState);
    if (!options.length) {
      message.warning("当前没有可选辅料");
      return;
    }
    openChoiceWhenEffectsComplete({
      kind: "consumable",
      options,
    });
  };

  const chooseGraph = (graph: PropRuntimeGraph) => {
    if (!manager) return;
    clearPendingChoice();
    if (choiceKind === "recipe") {
      manager.acquireProp(graph);
      setChoiceOpen(false);
      setChoiceOptions([]);
      setFlowStage("beforeRound");
      setRoundRunning(false);
      message.success(`获得配方：${graph.name || graph._id}`);
      return;
    }
    if (choiceKind === "material") {
      manager.acquireProp(graph);
      setChoiceOpen(false);
      setChoiceOptions([]);
      message.success(`获得原料：${graph.name || graph._id}`);
      if (finishRoundReward(manager)) setRoundRunning(false);
      return;
    }
    manager.acquireProp(graph);
    setLastConsumableChoiceRoundKey(getUpcomingRoundKey(manager));
    setChoiceOpen(false);
    setChoiceOptions([]);
    setFlowStage("beforeRound");
    message.success(`获得辅料：${graph.name || graph._id}`);
  };

  const skipChoice = () => {
    if (!manager || choiceKind !== "material") return;
    clearPendingChoice();
    setChoiceOpen(false);
    setChoiceOptions([]);
    message.info("跳过本轮原料选择");
    if (finishRoundReward(manager)) setRoundRunning(false);
  };

  const refreshChoiceOptions = () => {
    if (!manager || !choiceOpen || choiceRefreshing) return;
    setChoiceRefreshing(true);
    try {
      const currentState = manager.getState();
      const candidates = getChoiceCandidates(choiceKind, currentState);
      let forcedThirdGraphId = forceRefreshGraphIds[choiceKind];
      if (forcedThirdGraphId && !candidates.some((graph) => graph._id === forcedThirdGraphId)) {
        updateForceRefreshGraphId(choiceKind, undefined);
        forcedThirdGraphId = undefined;
      }
      const options = pickRefreshedChoiceGraphs(
        candidates,
        CHOICE_COUNT,
        currentState,
        choiceKind === "recipe" ? pendingCommissionIndex : undefined,
        { forcedThirdGraphId },
      );
      if (!options.length) {
        message.warning("没有可刷新的候选项");
        return;
      }
      setChoiceOptions(options);
    } finally {
      setChoiceRefreshing(false);
    }
  };

  const finishRoundReward = (targetManager: BattleManager): boolean => {
    const currentState = targetManager.getState();
    const currentLimit = getCommissionRoundLimit(currentState.commissionIndex, currentState.extraCommissionRounds);
    if (currentState.roundIndex < currentLimit) {
      setFlowStage("beforeRound");
      return true;
    }
    openCommissionSummary(targetManager);
    return false;
  };

  const openCommissionSummary = (targetManager: BattleManager) => {
    targetManager.endCommission();
    setCommissionSummary(buildCommissionSummary(targetManager, commissionSnapshot));
    setCommissionSummaryOpen(true);
    clearPendingChoice();
    setChoiceOpen(false);
    setChoiceOptions([]);
    setFlowStage("commissionSummary");
  };

  const continueAfterCommissionSummary = () => {
    if (!manager || !commissionSummary) return;
    setCommissionSummaryOpen(false);
    const nextCommissionIndex = commissionSummary.commissionIndex + 1;
    if (nextCommissionIndex > SANDBOX_COMMISSION_ROUNDS.length) {
      setFlowStage("gameSettling");
      manager.endGame();
      setFlowStage("gameEnded");
      setRoundRunning(false);
      message.success("游戏结算完成");
      return;
    }
    manager.startCommission(nextCommissionIndex);
    openRecipeChoice(manager, nextCommissionIndex);
  };

  const runNextRound = () => {
    if (!manager) return;
    if (roundRunning) return;
    if (flowStage !== "beforeRound") {
      message.warning(getBlockedRoundMessage(flowStage));
      return;
    }
    const currentState = manager.getState();
    const currentLimit = getCommissionRoundLimit(currentState.commissionIndex, currentState.extraCommissionRounds);
    if (currentState.roundIndex >= currentLimit) {
      if (finishRoundReward(manager)) setRoundRunning(false);
      return;
    }
    const roundRunRequestId = roundRunRequestIdRef.current + 1;
    roundRunRequestIdRef.current = roundRunRequestId;
    setRoundRunning(true);
    try {
      manager.runRound();
      const nextState = manager.getState();
      const nextLimit = getCommissionRoundLimit(nextState.commissionIndex, nextState.extraCommissionRounds);
      if (nextState.roundIndex >= nextLimit) {
        waitForMagicGainEffects(CHOICE_ANIMATION_FALLBACK_MS).then(() => {
          if (roundRunRequestIdRef.current !== roundRunRequestId) return;
          openCommissionSummary(manager);
        });
        return;
      }
      openMaterialChoice(manager, roundRunRequestId);
    } catch (err: any) {
      setRoundRunning(false);
      message.error(err?.message ?? String(err));
    }
  };

  const selectInstance = (item?: RuntimePropInstance) => {
    if (!item) {
      setSelectedInstanceId("");
      return;
    }
    setSelectedInstanceId(item.id);
    if (item.row >= 0 && item.col >= 0) setSelectedCell({ row: item.row, col: item.col });
  };

  const selectConsumableSlot = (item?: RuntimePropInstance) => {
    if (item) {
      selectInstance(item);
      return;
    }
    if (!manager || !gameStarted) {
      message.warning("请先开始游戏");
      return;
    }
    if (flowStage !== "beforeRound") {
      message.warning("只能在每轮开始前选择辅料");
      return;
    }
    if (roundRunning) {
      message.warning("鏈疆璋冨拰灏氭湭缁撴潫");
      return;
    }
    if (currentConsumableCount >= HAND_SLOT_COUNT) {
      message.warning("辅料栏已满");
      return;
    }
    const upcomingRoundKey = getUpcomingRoundKey(manager);
    if (lastConsumableChoiceRoundKey === upcomingRoundKey) {
      message.warning("本轮开始前已经选择过辅料");
      return;
    }
    openConsumableChoice(manager);
  };

  const choiceTitle = getChoiceTitle(choiceKind, pendingCommissionIndex);
  const canChooseConsumable = Boolean(
    manager
    && gameStarted
    && !roundRunning
    && flowStage === "beforeRound"
    && currentConsumableCount < HAND_SLOT_COUNT
    && lastConsumableChoiceRoundKey !== getUpcomingRoundKey(manager),
  );

  return (
    <SandboxRoot>
      <SandboxToolbar>
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ margin: 0 }}>游戏沙盒</Title>
          <Text type="secondary">使用 game 端 BattleManager 和 PropsMechanicsEngine 执行真实运行时逻辑</Text>
        </Space>
        <Space wrap>
          {loading && <Spin size="small" />}
          <Tag color={catalog.length ? "blue" : "default"}>运行时道具 {catalog.length}</Tag>
          <Select
            mode="multiple"
            value={selectedSystemTags}
            options={systemOptions}
            placeholder="选择3个体系"
            optionFilterProp="label"
            maxTagCount={3}
            disabled={gameStarted}
            style={{ width: 260, maxWidth: "100%" }}
            onChange={(values) => setSelectedSystemTags(values.slice(0, 3))}
          />
          {gameStarted ? (
            <Button danger onClick={endSandbox}>{flowStage === "gameEnded" ? "重置" : "结束游戏"}</Button>
          ) : (
            <Button icon={<ReloadOutlined />} onClick={startSandbox} type="primary">开始游戏</Button>
          )}
        </Space>
      </SandboxToolbar>

      {loadError && (
        <Alert
          type="error"
          message="运行时道具数据加载失败"
          description={loadError}
          showIcon
        />
      )}

      <StatsBar manager={manager} />
      <MagicGainEffects manager={manager} onPlayingChange={setMagicEffectPlaying} />
      <StatusPanel manager={manager} />

      <SandboxLayout>
        <SandboxColumn>
          <BoardPanel
            manager={manager}
            catalog={catalog}
            selectedCell={selectedCell}
            selectedInstanceId={selectedInstanceId}
            onSelectCell={setSelectedCell}
            onSelectInstance={selectInstance}
          />
          <ConsumablesPanel
            manager={manager}
            catalog={catalog}
            slotCount={HAND_SLOT_COUNT}
            selectedId={selectedInstanceId}
            onSelect={selectConsumableSlot}
            canChooseEmptySlot={canChooseConsumable}
          />
          <RecipesPanel
            manager={manager}
            catalog={catalog}
            slotCount={HAND_SLOT_COUNT}
            selectedId={selectedInstanceId}
            onSelect={selectInstance}
          />
        </SandboxColumn>
        <SandboxColumn>
          <MaterialLibraryPanel
            manager={manager}
            catalog={catalog}
            selectedId={selectedInstanceId}
            onSelect={selectInstance}
            removeDisabled={!gameStarted || roundRunning || choiceOpen || magicEffectPlaying}
          />
          <HarmonyButton
            type="primary"
            disabled={!gameStarted || roundRunning || flowStage !== "beforeRound"}
            onClick={runNextRound}
          >
            <span className="harmony-title">调和</span>
            <span className="harmony-subtitle">{getRoundButtonText(flowStage, currentRoundIndex, roundLimit)}</span>
          </HarmonyButton>
        </SandboxColumn>
        <EventLogPanel manager={manager} />
      </SandboxLayout>

      <ChoiceModal
        open={choiceOpen}
        title={choiceTitle}
        options={choiceOptions}
        catalog={catalog}
        allowSkip={choiceKind === "material"}
        skipText="跳过原料"
        onSkip={skipChoice}
        onChoose={chooseGraph}
        onRefresh={refreshChoiceOptions}
        refreshDisabled={choiceRefreshing}
        forceRefreshOptions={forceRefreshOptions}
        forceRefreshValue={forceRefreshValue}
        onForceRefreshChange={(graphId) => updateForceRefreshGraphId(choiceKind, graphId)}
        onCancel={() => message.warning(choiceKind === "material" ? "请选择或跳过原料" : "该选择不可跳过")}
      />
      <CommissionSummaryModal
        open={commissionSummaryOpen}
        data={commissionSummary}
        isLastCommission={(commissionSummary?.commissionIndex ?? 0) >= SANDBOX_COMMISSION_ROUNDS.length}
        onNext={continueAfterCommissionSummary}
      />
    </SandboxRoot>
  );
}

interface CommissionSnapshot {
  totalMagic: number;
  materialCount: number;
  recipeCount: number;
  consumableCount: number;
}

function createCommissionSnapshot(state: ReturnType<BattleManager["getState"]>): CommissionSnapshot {
  return {
    totalMagic: state.totalMagic,
    materialCount: state.materialLibrary.length,
    recipeCount: state.recipes.length,
    consumableCount: state.consumables.length,
  };
}

function getChoiceCandidates(
  kind: SandboxChoiceKind,
  state: ReturnType<BattleManager["getState"]>,
): PropRuntimeGraph[] {
  if (kind === "recipe") {
    const ownedRecipeIds = new Set(
      state.recipes
        .map((id) => state.items.find((item) => item.id === id)?.graph._id)
        .filter(Boolean) as string[],
    );
    return state.catalog.filter((graph) => (
      graph.type === 3 && !graph.derived && !ownedRecipeIds.has(graph._id)
    ));
  }
  if (kind === "material") {
    return state.catalog.filter((graph) => (
      graph.type === 1 && !graph.derived && graph.name !== EMPTY_PROP_NAME
    ));
  }
  return state.catalog.filter((graph) => graph.type === 2 && !graph.derived);
}

function buildCommissionSummary(
  manager: BattleManager,
  snapshot?: CommissionSnapshot,
): CommissionSummaryData {
  const state = manager.getState();
  const safeSnapshot = snapshot ?? createCommissionSnapshot(state);
  const roundLimit = getCommissionRoundLimit(state.commissionIndex, state.extraCommissionRounds);
  return {
    commissionIndex: state.commissionIndex,
    roundLimit,
    commissionMagic: state.totalMagic - safeSnapshot.totalMagic,
    totalMagic: state.totalMagic,
    materialCount: state.materialLibrary.length,
    recipeCount: state.recipes.length,
    consumableCount: state.consumables.length,
    addedMaterialCount: Math.max(0, state.materialLibrary.length - safeSnapshot.materialCount),
    addedRecipeCount: Math.max(0, state.recipes.length - safeSnapshot.recipeCount),
    addedConsumableCount: Math.max(0, state.consumables.length - safeSnapshot.consumableCount),
  };
}

function getScopedCatalog(catalog: PropRuntimeGraph[], selectedSystemTags: number[]): PropRuntimeGraph[] {
  const allowedTags = new Set([...selectedSystemTags, JUNK_TAG_ID]);
  return catalog.filter((graph) => (
    graph.name === EMPTY_PROP_NAME || (graph.tags ?? []).some((tag) => allowedTags.has(tag))
  ));
}

function getChoiceTitle(kind: SandboxChoiceKind, commissionIndex: number): string {
  switch (kind) {
    case "recipe":
      return `第 ${commissionIndex} 个委托：选择配方`;
    case "material":
      return "本轮结束：选择原料";
    case "consumable":
      return "回合开始前：选择辅料";
    default:
      return "选择道具";
  }
}

function getRoundButtonText(stage: SandboxFlowStage, roundIndex: number, roundLimit: number): string {
  if (stage === "beforeRound") return `开始第 ${Math.min(roundIndex + 1, roundLimit)} / ${roundLimit} 轮`;
  if (stage === "recipeChoice") return "请选择配方";
  if (stage === "materialChoice") return "请选择原料";
  if (stage === "commissionSummary") return "查看委托结算";
  if (stage === "gameSettling") return "结算中";
  if (stage === "gameEnded") return "游戏已结束";
  return "开始下一轮";
}

function getBlockedRoundMessage(stage: SandboxFlowStage): string {
  if (stage === "recipeChoice") return "请先选择本委托配方";
  if (stage === "materialChoice") return "请先完成本轮原料选择";
  if (stage === "commissionSummary") return "请先处理委托结算";
  if (stage === "gameEnded") return "游戏已结束";
  return "当前阶段不能开始回合";
}

function getUpcomingRoundKey(manager: BattleManager): string {
  const state = manager.getState();
  return `${state.commissionIndex}:${state.roundIndex + 1}`;
}

function readForceRefreshGraphIds(): ForceRefreshGraphIds {
  try {
    const raw = window.localStorage.getItem(FORCE_REFRESH_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      material: typeof parsed?.material === "string" ? parsed.material : undefined,
      consumable: typeof parsed?.consumable === "string" ? parsed.consumable : undefined,
      recipe: typeof parsed?.recipe === "string" ? parsed.recipe : undefined,
    };
  } catch {
    return {};
  }
}

function writeForceRefreshGraphIds(value: ForceRefreshGraphIds): void {
  try {
    window.localStorage.setItem(FORCE_REFRESH_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures. The selector still works for the current page lifetime.
  }
}
